<script lang="ts">
  import { cn } from "$lib/utils";
  import { mode, resetMode } from "mode-watcher";
  import { Mail } from "lucide-svelte";
  import logoNormal from "$lib/images/logo/logo_light_mono.svg";
  import logoDark from "$lib/images/logo/logo_dark_mono.svg";
  import { page } from "$app/state";
  import { onMount } from "svelte";
  import Button from "$lib/components/landing/redesigned/components/Button.svelte";

  let { fullWidth = true } = $props();

  type Section = {
    title: string;
    items: { label: string; href: string; newTab?: boolean; disabled?: boolean }[];
  };

  const isBlogPage = $derived(page.url.pathname.startsWith("/blog"));

  const footerSections: Section[] = [
    {
      title: "Features",
      items: [
        { label: "AI Web Scraping", href: "/features/ai-web-scraping" },
        { label: "AI PDF Processing", href: "/features/ai-pdf-processing" },
      ],
    },
    {
      title: "Use Cases",
      items: [
        { label: "AI E-commerce Automation", href: "/use-cases/ai-ecommerce-automation" },
        { label: "AI Resume Screening", href: "/use-cases/recruiting-ai-resume-screening" },
      ],
    },
    {
      title: "Resources",
      items: [{ label: "Affiliate Program", href: "/affiliates" }],
    },
    {
      title: "Company",
      items: [
        { label: "Blog", href: "/blog" },
        { label: "Pricing", href: "/plans" },
        { label: "About", href: "/about" },
      ],
    },
    {
      title: "Discover",
      items: [{ label: "Companies", href: "/companies" }],
    },
    {
      title: "Legal",
      items: [
        { label: "Master subscription agreement", href: "/msa" },
        { label: "Terms of use", href: "/policies/terms" },
        { label: "Privacy Policy", href: "/policies/privacy" },
      ],
    },
  ];

  let logo = $derived($mode !== "dark" ? logoNormal : logoDark);

  function copyToClipboard(event: MouseEvent) {
    const email = "help@chatsheet.com";
    navigator.clipboard
      .writeText(email)
      .then(() => {
        const target = event.target as HTMLElement;
        if (target && target.getBoundingClientRect) {
          const rect = target.getBoundingClientRect();
          const notification = document.createElement("div");
          notification.innerText = "We're here for you 😎";
          notification.style.cssText = `
            position: absolute;
            top: ${rect.top + window.scrollY}px;
            left: ${rect.right + window.scrollX + 10}px;
            padding: 8px 12px;
            background-color: #222;
            color: white;
            border-radius: 6px;
            font-size: 0.875rem;
            z-index: 1000;
            white-space: nowrap;
          `;
          document.body.appendChild(notification);

          setTimeout(() => {
            document.body.removeChild(notification);
          }, 2000);
        }
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
    event.preventDefault();
  }
</script>

<footer class="w-full bg-black text-white px-6 py-16 lg:py-24">
  <div class="mx-auto max-w-7xl">
    <!-- Mobile Layout -->
    <div class="grid grid-cols-1 gap-12 lg:hidden">
      <!-- Logo -->
      <div>
        <img src={logoDark} alt="Chatsheet logo" class="mb-8 w-[11rem]" />
      </div>

      <!-- Footer Links -->
      <div class="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3">
        {#each footerSections as section}
          <div>
            <h3 class="mb-4 text-lg font-semibold text-white">
              {section.title}
            </h3>
            <ul class="space-y-3">
              {#each section.items as item}
                <li>
                  {#if item.disabled}
                    <span class="text-neutral-200 opacity-50">
                      {item.label}
                    </span>
                  {:else}
                    <a
                      href={item.href}
                      class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
                      target={item.newTab ? "_blank" : "_self"}
                    >
                      {item.label}
                    </a>
                  {/if}
                </li>
              {/each}
            </ul>
          </div>
        {/each}
      </div>

      <!-- Help Section -->
      <div
        class="relative rounded-2xl overflow-hidden border border-white/5 bg-dark-gradient border-b-transparent p-6 max-w-sm"
      >
        <h3 class="text-xl font-semibold text-white mb-2">1-1 Help</h3>
        <p class="text-neutral-200 mb-4">
          <!-- <Mail strokeWidth={1.5} size={18} class="mr-2 inline-block" /> -->
          <button class="opacity-70 hover:opacity-100 hover:text-white transition-all" onclick={copyToClipboard}>
            help@chatsheet.com
          </button>
        </p>
        <Button
          variant="primary"
          size="lg"
          icon="send-wrapped"
          iconPosition="right"
          onclick={() => (window.location.href = "mailto:help@chatsheet.com")}>Get in Touch</Button
        >
      </div>

      <!-- Copyright and Social -->
      <div class="border-t border-white/5 pt-8">
        <div class="flex flex-col gap-6">
          <div class="flex items-center gap-6">
            <a
              href="https://linkedin.com/company/chatsheet"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="LinkedIn"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@chatsheet"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="YouTube"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                />
              </svg>
            </a>
            <a
              href="https://x.com/chatsheetai"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="Twitter"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                />
              </svg>
            </a>
          </div>
          <p class="text-neutral-200 opacity-70">
            ©{new Date().getFullYear()} Chatsheet. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>

    <!-- Desktop Layout -->
    <div class="hidden lg:block">
      <div class="flex justify-between items-start gap-16">
        <!-- Left Side - Logo and Help -->
        <div class="flex flex-col gap-8 max-w-sm">
          <img src={logoDark} alt="Chatsheet logo" class="w-[12rem]" />

          <!-- Help Section -->
          <div
            class="relative rounded-2xl overflow-hidden border border-white/5 bg-dark-gradient border-b-transparent p-6"
          >
            <h3 class="text-xl font-semibold text-white mb-2">1-1 Help</h3>
            <p class="text-neutral-200 mb-4">
              <!-- <Mail strokeWidth={1.5} size={18} class="mr-2 inline-block" /> -->
              <button class="opacity-70 hover:opacity-100 hover:text-white transition-all" onclick={copyToClipboard}>
                help@chatsheet.com
              </button>
            </p>
            <Button
              variant="primary"
              size="lg"
              icon="send-wrapped"
              iconPosition="right"
              onclick={() => (window.location.href = "mailto:help@chatsheet.com")}>Get in Touch</Button
            >
          </div>
        </div>

        <!-- Right Side - Footer Links -->
        <div class="flex-1">
          <div class="grid grid-cols-3 gap-x-12 gap-y-12 max-w-4xl">
            {#each footerSections as section}
              <div>
                <h3 class="mb-6 text-lg font-semibold text-white">
                  {section.title}
                </h3>
                <ul class="space-y-4">
                  {#each section.items as item}
                    <li>
                      {#if item.disabled}
                        <span class="text-neutral-200 opacity-50">
                          {item.label}
                        </span>
                      {:else}
                        <a
                          href={item.href}
                          class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
                          target={item.newTab ? "_blank" : "_self"}
                        >
                          {item.label}
                        </a>
                      {/if}
                    </li>
                  {/each}
                </ul>
              </div>
            {/each}
          </div>
        </div>
      </div>

      <!-- Bottom Section - Copyright and Social -->
      <div class="border-t border-white/5 mt-16 pt-8">
        <div class="flex justify-between items-center">
          <p class="text-neutral-200 opacity-70">
            ©{new Date().getFullYear()} Chatsheet. All Rights Reserved.
          </p>

          <div class="flex items-center gap-6">
            <a
              href="https://x.com/chatsheetai"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="Twitter"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.080l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@chatsheet"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="YouTube"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                />
              </svg>
            </a>
            <a
              href="https://linkedin.com/company/chatsheet"
              class="text-neutral-200 opacity-70 hover:opacity-100 hover:text-white transition-all"
              aria-label="LinkedIn"
            >
              <svg class="size-6" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</footer>

<style>
  .bg-dark-gradient {
    background: linear-gradient(182deg, #0c0a09 1.6%, #080706 99.98%);
  }
</style>
